import { useAppContext } from "@/game/AppContext";
import type { Room } from "@/game/types/Game";
import type { RoomID } from "@/realize-shared/model/Types";
import * as RoomManager from "@/game/events/RoomManager";
import React, { useContext } from "react";
import { ReactGameContext } from "@/game/GameContext";
import GameViewer from "./InAppGameViewer";
import { useRoom } from "@/game/events/useRoom";
import type { ProcessedEvent } from "@/game/events/ProcessedEvent";
import type { Type } from "@/realize-shared/model/Event";
import SendViewGameReq from "@/api/ViewerRequests";
import social from "@/api/social";
import { AuthContext } from "@/Realize";
import type { Socket } from "socket.io-client";
import type { ClientToServerEvents, ServerToClientEvents } from "@/local-shared/socket-events";


export default function Spectate({
    roomID,
    socket,
    exit,
}: {
    roomID: RoomID;
    socket: Socket<ServerToClientEvents, ClientToServerEvents>;
    exit: () => void;
})
{
    const { humanID } = useAppContext();

    const [spectator_id, setSpectatorId] = React.useState<number|null>(null);

    const room = React.useMemo<Room>(() =>
    {
        return RoomManager.buildRoom(roomID, humanID, false);
    }, [humanID, roomID]);

    const gameData = useRoom(room);

    const userData = useContext(AuthContext);


    React.useEffect(() =>
    {
        let pollingActive = true; // You can toggle this to stop polling later

        const poll = async () =>
        {
            if (!pollingActive) return;

            Logger.log("fetch");

            const events = room.data.events;
            const lastEvent = events[events.length - 1] as ProcessedEvent<Type> | undefined;
            const lastEventID = lastEvent ? lastEvent.id : null;

            try
            {
                const res = await SendViewGameReq(roomID, lastEventID);

                Logger.log("REQUEST SUCCESSFUL:", res);

                const incomingData = res.events;

                if (incomingData.length > 0)
                {
                    room.data.receiveData(incomingData);
                }
            }
            catch (err)
            {
                Logger.log("REQUEST FAILED", err);
            }

            // Schedule the next poll after a delay (e.g., 500ms)
            setTimeout(poll, 1000 / 2);
        };

        poll().catch((e) => void Logger.error(e));

        // Return a cleanup function
        return () =>
        {
            Logger.log("UNMOUNT");
            pollingActive = false;
        };
    }, [roomID, room]);

    React.useEffect(() =>
    {
        if (spectator_id !== null) return;

        social.rooms.addSpectator(room.id.toString()).then((data) =>
        {
            Logger.trace("Spectator added", data);
            setSpectatorId(data.spectatorId);
        })
            .catch((error) =>
            {
                Logger.error("Failed to increment spectator count", error);
            });
    }, [room.id, spectator_id, userData]);

    React.useEffect(() =>
    {
        return () =>
        {
            Logger.log("Unmounting Spectate component, removing spectator");

            if (spectator_id !== null)
            {
                social.rooms.removeSpectator(spectator_id).catch((error) =>
                {
                    Logger.error("Failed to decrement spectator count", error);
                });
            }
        };
    }, [room.id, spectator_id]);

    React.useEffect(() =>
    {
        const unloadWindow = () =>
        {
            if (spectator_id !== null)
            {
                social.rooms.removeSpectator(spectator_id).catch((error) =>
                {
                    Logger.error("Failed to decrement spectator count", error);
                });
            }
        };

        window.addEventListener("beforeunload", unloadWindow);

        return () =>
        {
            // Decrement the room's spectator count when the component unmounts
            window.removeEventListener("beforeunload", unloadWindow);
        };
    }, [spectator_id]);

    return (
        <ReactGameContext.Provider
            value={gameData}
        >
            <GameViewer
                socket={socket}
                exit={exit}
            />
        </ReactGameContext.Provider>
    );
}
